"use client";

import { ChevronLeft, ChevronRight, Plus, Sparkles, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState, useTransition } from "react";

import { useAuthGate } from "@/app/providers/AuthGateProvider";
import { CalendarWeek } from "@/components/calendar/CalendarWeek";
import { MealSuggestSheet } from "@/components/calendar/MealSuggestSheet";
import { PlanMealSheet } from "@/components/calendar/PlanMealSheet";
import { ShareCalendarModal } from "@/components/calendar/ShareCalendarModal";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { useRouter } from "@/i18n/navigation";
import type { CalendarEntryWithRecipe } from "@/lib/calendar/server";
import {
  addDays,
  formatWeekEyebrow,
  isCurrentWeek,
  parseIsoDate,
  toIsoDate,
} from "@/lib/calendar/week";

interface CalendarSurfaceProps {
  locale: string;
  weekStartIso: string;
  todayIso: string;
  entries: CalendarEntryWithRecipe[];
  isGuest?: boolean;
}

function defaultPlanCookOn(weekStartIso: string, todayIso: string): string {
  const weekStart = parseIsoDate(weekStartIso);
  const today = parseIsoDate(todayIso);
  if (!weekStart || !today) return weekStartIso;
  return isCurrentWeek(weekStart, today) ? todayIso : toIsoDate(addDays(weekStart, 0));
}

export function CalendarSurface({ locale, weekStartIso, todayIso, entries }: CalendarSurfaceProps) {
  const t = useTranslations("calendar");
  const router = useRouter();
  const { requireAuth } = useAuthGate();
  const [isPending, startTransition] = useTransition();
  const [planTarget, setPlanTarget] = useState<string | null>(null);
  const [planMounted, setPlanMounted] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestMounted, setSuggestMounted] = useState(false);

  useEffect(() => {
    if (planTarget) setPlanMounted(true);
  }, [planTarget]);

  useEffect(() => {
    if (suggestOpen) setSuggestMounted(true);
  }, [suggestOpen]);

  const weekStart = parseIsoDate(weekStartIso) ?? new Date();
  const today = parseIsoDate(todayIso) ?? new Date();
  const showThisWeek = isCurrentWeek(weekStart, today);
  const headingText = showThisWeek ? t("thisWeek") : formatWeekEyebrow(weekStart, locale);

  const navigateWeek = (delta: number) => {
    const target = addDays(weekStart, delta * 7);
    startTransition(() => {
      router.push(`/calendar?weekOf=${toIsoDate(target)}`);
    });
  };

  const goToToday = () => {
    startTransition(() => {
      router.push(`/calendar?weekOf=${todayIso}`);
    });
  };

  const openPlanGeneric = () => {
    if (!requireAuth()) return;
    setPlanTarget(defaultPlanCookOn(weekStartIso, todayIso));
  };
  const openPlanForDay = (cookOn: string) => {
    if (!requireAuth()) return;
    setPlanTarget(cookOn);
  };
  const closePlan = () => setPlanTarget(null);

  const openShare = () => {
    if (!requireAuth()) return;
    setShareOpen(true);
  };

  const openSuggest = () => {
    if (!requireAuth()) return;
    setSuggestOpen(true);
  };

  const isNavigating = isPending;

  const headerButtons = (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={openShare}
        aria-label={t("shareCalendar")}
      >
        <Users className="size-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" onClick={openSuggest} aria-label={t("suggestMeals")}>
        <Sparkles className="size-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" onClick={openPlanGeneric} aria-label={t("planMeal")}>
        <Plus className="size-4" aria-hidden />
      </Button>
    </div>
  );

  return (
    <>
      <PageHeader rightSlot={headerButtons} />
      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col px-3 pb-24">
        <header className="flex items-center justify-between gap-3 pt-4 pb-2 md:justify-start md:gap-6 md:pt-8">
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">{headingText}</h1>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => navigateWeek(-1)}
              disabled={isNavigating}
              aria-label={t("prevWeek")}
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={goToToday}
              disabled={isNavigating}
            >
              {t("today")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => navigateWeek(1)}
              disabled={isNavigating}
              aria-label={t("nextWeek")}
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
          <div className="hidden md:block md:ms-auto">{headerButtons}</div>
        </header>
        <CalendarWeek
          locale={locale}
          weekStartIso={weekStartIso}
          todayIso={todayIso}
          entries={entries}
          isNavigating={isNavigating}
          onOpenPlanSheet={openPlanForDay}
        />
      </main>
      {planMounted && (
        <PlanMealSheet
          isOpen={planTarget !== null}
          onClose={closePlan}
          cookOn={planTarget ?? defaultPlanCookOn(weekStartIso, todayIso)}
          locale={locale}
        />
      )}
      <ShareCalendarModal isOpen={shareOpen} onClose={() => setShareOpen(false)} />
      {suggestMounted && (
        <MealSuggestSheet
          isOpen={suggestOpen}
          onClose={() => setSuggestOpen(false)}
          locale={locale}
          weekStartIso={weekStartIso}
          todayIso={todayIso}
        />
      )}
    </>
  );
}
